import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  Share2,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Check,
  X,
  Eye,
  EyeOff,
  Shield,
  KeyRound,
  Smartphone,
  Lock,
  ExternalLink,
  Link as LinkIcon,
  User,
  Zap,
  Tag,
  Percent,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  CreditCard,
  Settings as SettingsIcon,
} from "lucide-react";
import { api, ApiError, type SocialLink, type AdminPremiumPackage, type AiProviderRow, type AiProviderCreatePayload, type AiProviderUpdatePayload } from "../../lib/api";
import { PROVIDER_TYPE_PRESETS } from "../../lib/aiProvidersApi";
import { ConfirmDialog } from "../../components/admin/AdminModals";
import { IngestionSettingsSection } from "../../components/admin/IngestionSettingsSection";
import { useAdminToken, PageLoader, PageError } from "./adminShared";

// ─── Section 4: Social Links Management ─────────────────────────────────────

function SocialLinksSettingsSection({ token }: { token: string }) {
  const [links, setLinks] = useState<SocialLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingLink, setEditingLink] = useState<SocialLink | null>(null);

  // Form fields
  const [platform, setPlatform] = useState("telegram");
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [sortOrder, setSortOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [okMsg, setOkMsg] = useState("");

  const loadLinks = useCallback((silent = false) => {
    if (!token) return;
    if (!silent) setLoading(true);
    api.admin.socialLinks
      .list(token)
      .then((data) => setLinks(data))
      .catch((e) => setErrorMsg(e instanceof ApiError ? e.message : "Failed to load links"))
      .finally(() => {
        if (!silent) setLoading(false);
      });
  }, [token]);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  const resetForm = () => {
    setPlatform("telegram");
    setTitle("");
    setUrl("");
    setSortOrder(links.length + 1);
    setIsActive(true);
    setEditingLink(null);
    setShowAddForm(false);
    setErrorMsg("");
  };

  const startEdit = (link: SocialLink) => {
    setEditingLink(link);
    setPlatform(link.platform);
    setTitle(link.title);
    setUrl(link.url);
    setSortOrder(link.sortOrder);
    setIsActive(link.isActive);
    setShowAddForm(true);
    setErrorMsg("");
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !url.trim() || !token) return;
    setSubmitting(true);
    setErrorMsg("");
    setOkMsg("");

    try {
      if (editingLink) {
        const updated = await api.admin.socialLinks.update(token, editingLink.id, {
          platform,
          title: title.trim(),
          url: url.trim(),
          sort_order: Number(sortOrder) || 0,
          is_active: isActive,
        });
        setLinks((prev) => prev.map((l) => (l.id === editingLink.id ? updated : l)));
        setOkMsg(`Updated "${title}" successfully.`);
      } else {
        const created = await api.admin.socialLinks.create(token, {
          platform,
          title: title.trim(),
          url: url.trim(),
          sort_order: Number(sortOrder) || 0,
          is_active: isActive,
        });
        setLinks((prev) => [...prev, created]);
        setOkMsg(`Added "${title}" to footer links.`);
      }
      resetForm();
      loadLinks(true);
      setTimeout(() => setOkMsg(""), 3500);
    } catch (err) {
      setErrorMsg(err instanceof ApiError ? err.message : "Failed to save social link");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (link: SocialLink) => {
    if (!token) return;
    // Optimistic toggle
    setLinks((prev) =>
      prev.map((l) => (l.id === link.id ? { ...l, isActive: !l.isActive } : l))
    );
    try {
      const updated = await api.admin.socialLinks.update(token, link.id, {
        is_active: !link.isActive,
      });
      setLinks((prev) => prev.map((l) => (l.id === link.id ? updated : l)));
    } catch (err) {
      // Revert on failure
      setLinks((prev) =>
        prev.map((l) => (l.id === link.id ? { ...l, isActive: link.isActive } : l))
      );
      setErrorMsg(err instanceof ApiError ? err.message : "Failed to toggle status");
    }
  };

  const [deleteTarget, setDeleteTarget] = useState<SocialLink | null>(null);

  const confirmDelete = async (link: SocialLink) => {
    if (!token) return;
    const target = link;
    setDeleteTarget(null);
    const prevLinks = links;
    setLinks((prev) => prev.filter((l) => l.id !== target.id));
    try {
      await api.admin.socialLinks.delete(token, target.id);
      loadLinks(true);
    } catch (err) {
      setLinks(prevLinks);
      setErrorMsg(err instanceof ApiError ? err.message : "Failed to delete link");
    }
  };

  return (
    <div className="bg-card rounded-xl border border-border shadow-xs p-6 space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap pb-4 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary border border-primary/20 flex items-center justify-center shrink-0">
            <Share2 size={20} />
          </div>
          <div>
            <h2 className="font-semibold text-lg">Social & Community Links</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              These channels and community links appear in the student platform footer and help sections.
            </p>
          </div>
        </div>
        {!showAddForm && (
          <button
            type="button"
            onClick={() => {
              resetForm();
              setShowAddForm(true);
            }}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-lg bg-primary text-on-accent hover:opacity-90 transition shadow-xs"
          >
            <Plus size={15} />
            <span>Add Channel Link</span>
          </button>
        )}
      </div>

      {errorMsg && (
        <div className="p-3 text-xs bg-destructive/10 border border-destructive/20 text-destructive rounded-lg flex items-center gap-2">
          <AlertCircle size={15} className="shrink-0" />
          <span>{errorMsg}</span>
        </div>
      )}
      {okMsg && (
        <div className="p-3 text-xs bg-success/10 border border-success/30 text-success rounded-lg flex items-center gap-2">
          <CheckCircle2 size={15} className="shrink-0" />
          <span>{okMsg}</span>
        </div>
      )}

      {showAddForm && (
        <form onSubmit={handleSave} className="bg-secondary/40 border border-border rounded-xl p-4.5 space-y-4">
          <div className="flex items-center justify-between pb-2 border-b border-border/60">
            <h3 className="font-semibold text-sm">
              {editingLink ? `Edit "${editingLink.title}"` : "Add New Channel Link"}
            </h3>
            <button
              type="button"
              onClick={resetForm}
              className="text-muted-foreground hover:text-foreground text-xs p-1"
            >
              <X size={16} />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Platform</label>
              <select
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                className="w-full text-xs rounded-lg border border-border bg-background px-3 py-2 text-foreground"
              >
                <option value="telegram">Telegram (Channel / Bot)</option>
                <option value="youtube">YouTube</option>
                <option value="tiktok">TikTok</option>
                <option value="facebook">Facebook</option>
                <option value="instagram">Instagram</option>
                <option value="website">Custom Website</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Display Title</label>
              <input
                type="text"
                placeholder="e.g. Soorgaa Official Channel"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full text-xs rounded-lg border border-border bg-background px-3 py-2 text-foreground"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-muted-foreground mb-1">Target URL</label>
              <input
                type="url"
                placeholder="https://t.me/Soorgaa_Official"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="w-full text-xs rounded-lg border border-border bg-background px-3 py-2 text-foreground font-mono"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Display Order</label>
              <input
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(Number(e.target.value))}
                className="w-full text-xs rounded-lg border border-border bg-background px-3 py-2 text-foreground"
              />
            </div>
          </div>

          <div className="flex items-center justify-between pt-2">
            <label className="flex items-center gap-2 text-xs font-medium cursor-pointer select-none">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="rounded border-border text-primary focus:ring-primary h-4 w-4"
              />
              <span>Visible in student footer</span>
            </label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={resetForm}
                className="text-xs px-3 py-1.5 rounded-lg border border-border hover:bg-muted text-muted-foreground"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="text-xs px-4 py-1.5 rounded-lg bg-primary text-on-accent font-semibold hover:opacity-90 disabled:opacity-50"
              >
                {submitting ? "Saving…" : editingLink ? "Update Link" : "Add Link"}
              </button>
            </div>
          </div>
        </form>
      )}

      {loading ? (
        <div className="py-8 flex items-center justify-center text-muted-foreground text-xs gap-2">
          <Loader2 size={16} className="animate-spin" />
          <span>Loading social channels…</span>
        </div>
      ) : links.length === 0 ? (
        <div className="py-8 text-center text-muted-foreground text-xs border border-dashed border-border rounded-xl">
          No social channel links configured yet. Add your Telegram channels or official handles.
        </div>
      ) : (
        <div className="divide-y divide-border border border-border rounded-xl overflow-hidden">
          {links.map((link) => (
            <div
              key={link.id}
              className={`p-3.5 flex items-center justify-between gap-3 text-xs transition ${
                link.isActive ? "bg-card" : "bg-muted/30 opacity-70"
              }`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="font-mono text-xs text-muted-foreground w-5 text-center">
                  #{link.sortOrder}
                </span>
                <span className="px-2 py-0.5 rounded-md text-xs font-semibold uppercase tracking-wider bg-secondary border border-border text-foreground">
                  {link.platform}
                </span>
                <div className="min-w-0">
                  <p className="font-semibold text-foreground truncate">{link.title}</p>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary hover:underline text-xs flex items-center gap-1 truncate"
                  >
                    <span>{link.url}</span>
                    <ExternalLink size={10} className="shrink-0" />
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => handleToggle(link)}
                  className={`text-xs px-2.5 py-1 rounded-md font-semibold border ${
                    link.isActive
                      ? "bg-success/10 text-success border-success/30 hover:bg-success/20"
                      : "bg-muted text-muted-foreground border-border hover:bg-muted/80"
                  }`}
                >
                  {link.isActive ? "Live" : "Hidden"}
                </button>
                <button
                  type="button"
                  onClick={() => startEdit(link)}
                  className="p-1.5 text-muted-foreground hover:text-foreground rounded-md hover:bg-muted"
                  title="Edit"
                >
                  <Pencil size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteTarget(link)}
                  className="p-1.5 text-destructive/80 hover:text-destructive rounded-md hover:bg-destructive/10"
                  title="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        title="Delete Social Channel Link"
        message={`Are you sure you want to remove "${deleteTarget?.title}" from the platform?`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={() => {
          if (deleteTarget) confirmDelete(deleteTarget);
        }}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}

// ─── Section: AI Providers Management (table-based CRUD) ─────────────────────

function AiProvidersSettingsSection({ token }: { token: string }) {
  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState<AiProviderRow[]>([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [testState, setTestState] = useState<Record<string, { loading: boolean; success?: boolean; message?: string }>>({});
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Form state
  const [formName, setFormName] = useState("");
  const [formType, setFormType] = useState("gemini");
  const [formBaseUrl, setFormBaseUrl] = useState("");
  const [formModel, setFormModel] = useState("");
  const [formApiKey, setFormApiKey] = useState("");
  const [formPriority, setFormPriority] = useState(1);
  const [formEnabled, setFormEnabled] = useState(true);
  const [formRpm, setFormRpm] = useState<number | "">("");
  const [formRpd, setFormRpd] = useState<number | "">("");
  const [formTpd, setFormTpd] = useState<number | "">("");
  const [formDefault, setFormDefault] = useState(false);
  const [keyVisible, setKeyVisible] = useState(false);
  const [formSaving, setFormSaving] = useState(false);
  const [formTesting, setFormTesting] = useState(false);

  const loadData = useCallback(() => {
    if (!token) return;
    setLoading(true);
    api.admin.aiProviders
      .list(token)
      .then((rows) => setProviders(rows))
      .catch((e) => setErrorMsg(e instanceof ApiError ? e.message : "Failed to load AI providers"))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const resetForm = () => {
    setFormName("");
    setFormType("gemini");
    setFormBaseUrl("");
    setFormModel("");
    setFormApiKey("");
    setFormPriority(1);
    setFormEnabled(true);
    setFormRpm("");
    setFormRpd("");
    setFormTpd("");
    setFormDefault(false);
    setKeyVisible(false);
  };

  const handleTypeChange = (t: string) => {
    setFormType(t);
    const preset = PROVIDER_TYPE_PRESETS[t];
    if (preset) {
      setFormBaseUrl(preset.base_url);
      setFormModel(preset.model);
    }
  };

  const startEdit = (p: AiProviderRow) => {
    setEditingId(p.id);
    setShowAddForm(true);
    setFormName(p.name);
    setFormType(p.provider_type);
    setFormBaseUrl(p.base_url || "");
    setFormModel(p.model);
    setFormApiKey(""); // blank = keep existing
    setFormPriority(p.priority);
    setFormEnabled(p.enabled);
    setFormRpm(p.rpm ?? "");
    setFormRpd(p.rpd ?? "");
    setFormTpd(p.tpd ?? "");
    setFormDefault(p.is_default);
    setKeyVisible(false);
  };

  const handleSave = async () => {
    setFormSaving(true);
    setErrorMsg("");
    setOkMsg("");
    try {
      if (editingId) {
        const payload: AiProviderUpdatePayload = {
          name: formName,
          provider_type: formType,
          base_url: formBaseUrl,
          model: formModel,
          enabled: formEnabled,
          priority: formPriority,
          rpm: formRpm === "" ? null : Number(formRpm),
          rpd: formRpd === "" ? null : Number(formRpd),
          tpd: formTpd === "" ? null : Number(formTpd),
          is_default: formDefault,
        };
        if (formApiKey.trim()) payload.api_key = formApiKey.trim();
        await api.admin.aiProviders.patch(token, editingId, payload);
        setOkMsg("Provider updated successfully.");
      } else {
        const payload: AiProviderCreatePayload = {
          name: formName,
          provider_type: formType,
          base_url: formBaseUrl,
          model: formModel,
          api_key: formApiKey,
          enabled: formEnabled,
          priority: formPriority,
          rpm: formRpm === "" ? null : Number(formRpm),
          rpd: formRpd === "" ? null : Number(formRpd),
          tpd: formTpd === "" ? null : Number(formTpd),
          is_default: formDefault,
        };
        await api.admin.aiProviders.create(token, payload);
        setOkMsg("Provider added successfully.");
      }
      setShowAddForm(false);
      setEditingId(null);
      resetForm();
      loadData();
    } catch (err: any) {
      setErrorMsg(err instanceof ApiError ? err.message : "Failed to save provider");
    } finally {
      setFormSaving(false);
    }
  };

  const handleTest = async (id: string) => {
    setTestState((prev) => ({ ...prev, [id]: { loading: true } }));
    try {
      const res = await api.admin.aiProviders.testById(token, id);
      setTestState((prev) => ({ ...prev, [id]: { loading: false, success: res.success, message: res.message } }));
      loadData(); // refresh last_test_status
    } catch (err: any) {
      setTestState((prev) => ({ ...prev, [id]: { loading: false, success: false, message: err.message || "Test failed" } }));
    }
  };

  const handleTestForm = async () => {
    // Test during form: save first if creating, then test
    setFormTesting(true);
    try {
      if (!editingId) {
        // For new provider, save first then test
        await handleSave();
        // After save, loadData runs; find the new provider
        const rows = await api.admin.aiProviders.list(token);
        const newest = rows.find((r) => r.name === formName);
        if (newest) {
          await handleTest(newest.id);
        }
      } else {
        // For existing, test directly
        await handleTest(editingId);
      }
    } catch (err: any) {
      setErrorMsg(err.message || "Test connection failed");
    } finally {
      setFormTesting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await api.admin.aiProviders.remove(token, id);
      setOkMsg("Provider deleted.");
      setDeleteConfirm(null);
      loadData();
    } catch (err: any) {
      setErrorMsg(err instanceof ApiError ? err.message : "Failed to delete provider");
    }
  };

  const handleToggleEnabled = async (p: AiProviderRow) => {
    try {
      await api.admin.aiProviders.patch(token, p.id, { enabled: !p.enabled });
      loadData();
    } catch (err: any) {
      setErrorMsg(err instanceof ApiError ? err.message : "Failed to toggle provider");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-12">
        <Loader2 className="animate-spin text-primary" size={24} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status banners */}
      {errorMsg && (
        <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-xs font-medium flex items-center gap-2">
          <AlertCircle size={15} className="shrink-0" />
          <span>{errorMsg}</span>
          <button onClick={() => setErrorMsg("")} className="ml-auto"><X size={14} /></button>
        </div>
      )}
      {okMsg && (
        <div className="p-3 rounded-lg bg-success/10 border border-success/20 text-success text-xs font-medium flex items-center gap-2">
          <CheckCircle2 size={15} className="shrink-0" />
          <span>{okMsg}</span>
          <button onClick={() => setOkMsg("")} className="ml-auto"><X size={14} /></button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-foreground">AI Providers</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Auto mode tries providers by priority, falling back on 429/quota/timeout.
          </p>
        </div>
        {!showAddForm && (
          <button
            type="button"
            onClick={() => { resetForm(); setEditingId(null); setShowAddForm(true); }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-on-accent text-xs font-bold hover:opacity-90 cursor-pointer"
          >
            <Plus size={14} />
            <span>Add provider</span>
          </button>
        )}
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="bg-card rounded-xl border border-border p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-bold text-foreground">{editingId ? "Edit Provider" : "Add Provider"}</h4>
            <button onClick={() => { setShowAddForm(false); setEditingId(null); resetForm(); }} className="text-muted-foreground hover:text-foreground cursor-pointer">
              <X size={16} />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
            {/* Name */}
            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Name</label>
              <input type="text" value={formName} onChange={(e) => setFormName(e.target.value)} className="field w-full px-2.5 py-1.5 rounded-md" placeholder="My Gemini Provider" />
            </div>

            {/* Provider Type */}
            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Provider Type</label>
              <select value={formType} onChange={(e) => handleTypeChange(e.target.value)} className="field w-full px-2.5 py-1.5 rounded-md">
                <option value="gemini">Gemini (Native)</option>
                <option value="groq">Groq</option>
                <option value="mistral">Mistral</option>
                <option value="openrouter">OpenRouter</option>
                <option value="ollama">Ollama (Local)</option>
                <option value="openai_compatible">OpenAI Compatible</option>
              </select>
            </div>

            {/* Base URL */}
            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Base URL</label>
              <input type="text" value={formBaseUrl} onChange={(e) => setFormBaseUrl(e.target.value)} className="field w-full px-2.5 py-1.5 rounded-md font-mono" placeholder="https://api.example.com/v1" />
            </div>

            {/* Model */}
            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Model</label>
              <input type="text" value={formModel} onChange={(e) => setFormModel(e.target.value)} className="field w-full px-2.5 py-1.5 rounded-md font-mono" placeholder="gemini-2.5-flash" />
            </div>

            {/* API Key */}
            {formType !== "ollama" && (
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">
                  API Key {editingId && <span className="text-muted-foreground normal-case font-normal">(blank = keep existing)</span>}
                </label>
                <div className="relative">
                  <input
                    type={keyVisible ? "text" : "password"}
                    value={formApiKey}
                    onChange={(e) => setFormApiKey(e.target.value)}
                    className="field w-full px-2.5 py-1.5 rounded-md font-mono pr-8"
                    placeholder={editingId ? "••••••••" : "Enter API key"}
                  />
                  <button type="button" onClick={() => setKeyVisible(!keyVisible)} className="absolute right-2 top-2 text-muted-foreground hover:text-foreground cursor-pointer">
                    {keyVisible ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>
            )}

            {/* Priority */}
            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Priority (1 = highest)</label>
              <input type="number" min={1} max={99} value={formPriority} onChange={(e) => setFormPriority(parseInt(e.target.value) || 1)} className="field w-full px-2.5 py-1.5 rounded-md" />
            </div>

            {/* Rate limits */}
            <div className="grid grid-cols-3 gap-2 col-span-1 sm:col-span-2">
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">RPM</label>
                <input type="number" min={1} value={formRpm} onChange={(e) => setFormRpm(e.target.value === "" ? "" : parseInt(e.target.value))} className="field w-full px-2 py-1.5 rounded-md font-mono" placeholder="30" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">RPD</label>
                <input type="number" min={1} value={formRpd} onChange={(e) => setFormRpd(e.target.value === "" ? "" : parseInt(e.target.value))} className="field w-full px-2 py-1.5 rounded-md font-mono" placeholder="1500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">TPD</label>
                <input type="number" min={1} value={formTpd} onChange={(e) => setFormTpd(e.target.value === "" ? "" : parseInt(e.target.value))} className="field w-full px-2 py-1.5 rounded-md font-mono" placeholder="" />
              </div>
            </div>

            {/* Toggles */}
            <div className="flex items-center gap-4 col-span-1 sm:col-span-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={formEnabled} onChange={(e) => setFormEnabled(e.target.checked)} className="rounded border-border text-primary w-4 h-4" />
                <span className="text-xs font-medium">Enabled</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={formDefault} onChange={(e) => setFormDefault(e.target.checked)} className="rounded border-border text-primary w-4 h-4" />
                <span className="text-xs font-medium">Set as default</span>
              </label>
            </div>
          </div>

          {/* Form actions */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-border/50">
            <button
              type="button"
              onClick={handleTestForm}
              disabled={formTesting || formSaving}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-secondary text-foreground hover:bg-secondary/80 text-xs font-semibold border border-border cursor-pointer"
            >
              {formTesting ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />}
              <span>Test connection</span>
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={formSaving}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-primary text-on-accent text-xs font-bold hover:opacity-90 cursor-pointer"
            >
              {formSaving && <Loader2 size={13} className="animate-spin" />}
              <span>{editingId ? "Save changes" : "Add provider"}</span>
            </button>
          </div>
        </div>
      )}

      {/* Providers Table */}
      {providers.length === 0 && !showAddForm ? (
        <div className="bg-card rounded-xl border border-border p-8 text-center">
          <Sparkles size={24} className="mx-auto text-muted-foreground mb-2" />
          <p className="py-6 text-center text-sm text-muted-foreground">No AI providers configured yet.</p>
          <p className="text-xs text-muted-foreground mt-1">Click "Add provider" to configure your first LLM backend.</p>
        </div>
      ) : (
        <div className="overflow-x-auto admin-scroll-x">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="py-2 pr-3 font-semibold text-muted-foreground">Name</th>
                <th className="py-2 pr-3 font-semibold text-muted-foreground">Type</th>
                <th className="py-2 pr-3 font-semibold text-muted-foreground">Model</th>
                <th className="py-2 pr-3 font-semibold text-muted-foreground">API Key</th>
                <th className="py-2 pr-3 font-semibold text-muted-foreground">Enabled</th>
                <th className="py-2 pr-3 font-semibold text-muted-foreground">Priority</th>
                <th className="py-2 pr-3 font-semibold text-muted-foreground">Last Test</th>
                <th className="py-2 pr-3 font-semibold text-muted-foreground text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {providers.map((p) => {
                const test = testState[p.id];
                return (
                  <tr key={p.id} className="border-b border-border/50 hover:bg-secondary/30">
                    <td className="py-2 pr-3">
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-foreground">{p.name}</span>
                        {p.is_default && (
                          <span className="text-xs font-bold px-1.5 py-0.5 rounded bg-primary/15 text-primary border border-primary/25">DEFAULT</span>
                        )}
                      </div>
                    </td>
                    <td className="py-2 pr-3 text-muted-foreground font-mono">{p.provider_type}</td>
                    <td className="py-2 pr-3 text-muted-foreground font-mono">{p.model}</td>
                    <td className="py-2 pr-3">
                      {p.has_key ? (
                        <span className="font-mono text-muted-foreground">{p.api_key_masked || "●●●●"}</span>
                      ) : p.provider_type === "ollama" ? (
                        <span className="text-muted-foreground italic">not needed</span>
                      ) : (
                        <span className="text-destructive">missing</span>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <button
                        type="button"
                        onClick={() => handleToggleEnabled(p)}
                        className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer ${p.enabled ? "bg-primary" : "bg-muted-foreground/30"}`}
                      >
                        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${p.enabled ? "left-[18px]" : "left-0.5"}`} />
                      </button>
                    </td>
                    <td className="py-2 pr-3 text-muted-foreground">{p.priority}</td>
                    <td className="py-2 pr-3">
                      {test?.loading ? (
                        <Loader2 size={12} className="animate-spin text-muted-foreground" />
                      ) : test ? (
                        <span className={test.success ? "text-success" : "text-destructive"}>{test.success ? "OK" : "Failed"}</span>
                      ) : p.last_test_status ? (
                        <span className={p.last_test_status === "ok" ? "text-success" : "text-destructive"}>
                          {p.last_test_status === "ok" ? "OK" : "Failed"}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <div className="flex items-center justify-end gap-1">
                        <button type="button" onClick={() => handleTest(p.id)} disabled={test?.loading} className="p-1 rounded hover:bg-secondary text-muted-foreground hover:text-foreground cursor-pointer" title="Test">
                          {test?.loading ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />}
                        </button>
                        <button type="button" onClick={() => startEdit(p)} className="p-1 rounded hover:bg-secondary text-muted-foreground hover:text-foreground cursor-pointer" title="Edit">
                          <Pencil size={13} />
                        </button>
                        <button type="button" onClick={() => setDeleteConfirm(p.id)} className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive cursor-pointer" title="Delete">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteConfirm && (
        <ConfirmDialog
          open
          title="Delete Provider"
          message="Are you sure you want to delete this AI provider? This cannot be undone."
          confirmLabel="Delete"
          variant="destructive"
          onConfirm={() => handleDelete(deleteConfirm)}
          onClose={() => setDeleteConfirm(null)}
        />
      )}
    </div>
  );
}

// ─── Main Admin Settings Component ──────────────────────────────────────────

type SettingSection = "profile" | "payments" | "ai" | "ingestion" | "discounts" | "social" | "system";

interface DiscountItemState {
  discount_price: number;
  original_price: number;
  discount_badge: string;
  is_active: boolean;
}

export function AdminSettings() {
  const token = useAdminToken();
  const [activeSection, setActiveSection] = useState<SettingSection>("payments");

  // Core Data
  const [settings, setSettings] = useState<Awaited<ReturnType<typeof api.admin.settings>> | null>(null);
  const [pay, setPay] = useState<Awaited<ReturnType<typeof api.admin.paymentSettings>> | null>(null);
  const [packages, setPackages] = useState<AdminPremiumPackage[]>([]);
  const [adminMe, setAdminMe] = useState<Awaited<ReturnType<typeof api.admin.me>> | null>(null);

  // Loading & Feedback
  const [loading, setLoading] = useState(true);
  const [savingPayment, setSavingPayment] = useState(false);
  const [savingDiscounts, setSavingDiscounts] = useState(false);
  const [testing, setTesting] = useState<"chapa" | "telebirr" | null>(null);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");

  // Payment Form States
  const [monthlyEtb, setMonthlyEtb] = useState("99");
  const [semesterEtb, setSemesterEtb] = useState("299");
  const [monthlyMonths, setMonthlyMonths] = useState("1");
  const [semesterMonths, setSemesterMonths] = useState("4");
  const [preferredProvider, setPreferredProvider] = useState("chapa");
  const [telebirrEnvironment, setTelebirrEnvironment] = useState("test");

  // Fastest Payment & Express Checkout
  const [fastestPaymentMethod, setFastestPaymentMethod] = useState("telebirr_direct");
  const [expressCheckoutEnabled, setExpressCheckoutEnabled] = useState(true);

  // Manual Transfer Numbers
  const [telebirrManualPhone, setTelebirrManualPhone] = useState("0967344843");
  const [telebirrManualName, setTelebirrManualName] = useState("Atinaf");
  const [telebirrManualEnabled, setTelebirrManualEnabled] = useState(true);
  const [cbeAccountNumber, setCbeAccountNumber] = useState("");
  const [cbeAccountName, setCbeAccountName] = useState("");
  const [cbeReceiveLink, setCbeReceiveLink] = useState("https://t.me/Soorgaa_bot");
  const [cbeManualEnabled, setCbeManualEnabled] = useState(true);
  const [manualInstructions, setManualInstructions] = useState("");
  const [manualPaymentEnabled, setManualPaymentEnabled] = useState(true);

  // Gateways
  const [chapaEnabled, setChapaEnabled] = useState(true);
  const [telebirrEnabled, setTelebirrEnabled] = useState(true);
  const [chapaSecretKey, setChapaSecretKey] = useState("");
  const [chapaWebhookSecret, setChapaWebhookSecret] = useState("");
  const [telebirrFabricAppId, setTelebirrFabricAppId] = useState("");
  const [telebirrAppSecret, setTelebirrAppSecret] = useState("");
  const [telebirrMerchantAppId, setTelebirrMerchantAppId] = useState("");
  const [telebirrMerchantCode, setTelebirrMerchantCode] = useState("");
  const [telebirrPrivateKey, setTelebirrPrivateKey] = useState("");
  const [telebirrPublicKey, setTelebirrPublicKey] = useState("");

  // Package Discounts Map: package_id -> DiscountItemState
  const [discountsMap, setDiscountsMap] = useState<Record<string, DiscountItemState>>({});

  // Profile Form States
  const [profileFullName, setProfileFullName] = useState("");
  const [profileCurrentPassword, setProfileCurrentPassword] = useState("");
  const [profileNewPassword, setProfileNewPassword] = useState("");
  const [profileConfirmPassword, setProfileConfirmPassword] = useState("");
  const [showCurrentPass, setShowCurrentPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [profileOkMsg, setProfileOkMsg] = useState("");

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    setError("");
    Promise.all([
      api.admin.settings(token),
      api.admin.paymentSettings(token),
      api.admin.me(token),
      api.admin.packages.list(token).catch(() => ({ packages: [] as AdminPremiumPackage[] })),
    ])
      .then(([s, p, me, pkgsRes]) => {
        setSettings(s);
        setPay(p);
        setAdminMe(me);
        setPackages("packages" in pkgsRes ? pkgsRes.packages : pkgsRes.items);

        setProfileFullName(me.fullName || "");
        setMonthlyEtb(String(p.monthlyEtb));
        setSemesterEtb(String(p.semesterEtb));
        setMonthlyMonths(String(p.monthlyMonths ?? 1));
        setSemesterMonths(String(p.semesterMonths ?? 4));
        setPreferredProvider(p.preferredProvider || "chapa");
        setTelebirrEnvironment(p.telebirrEnvironment || "test");

        setFastestPaymentMethod(p.fastestPaymentMethod || "telebirr_direct");
        setExpressCheckoutEnabled(p.expressCheckoutEnabled ?? true);

        setTelebirrManualPhone(p.telebirrManualPhone || "0967344843");
        setTelebirrManualName(p.telebirrManualName || "Atinaf");
        setCbeAccountNumber(p.cbeAccountNumber || "");
        setCbeAccountName(p.cbeAccountName || "");
        setCbeReceiveLink(p.cbeReceiveLink || "https://t.me/Soorgaa_bot");
        setManualInstructions(p.manualInstructions || "");
        setManualPaymentEnabled(p.manualPaymentEnabled ?? true);
        setTelebirrManualEnabled(p.telebirrManualEnabled ?? true);
        setCbeManualEnabled(p.cbeManualEnabled ?? true);
        setChapaEnabled(p.chapaEnabled ?? p.chapa?.enabled ?? true);
        setTelebirrEnabled(p.telebirrEnabled ?? p.telebirr?.enabled ?? true);

        // Build discount items mapping for packages
        const existingDiscounts = p.packageDiscounts || {};
        const initialMap: Record<string, DiscountItemState> = {};
        const packageItems = "packages" in pkgsRes ? pkgsRes.packages : pkgsRes.items;
        for (const pkg of packageItems || []) {
          const saved = existingDiscounts[pkg.id];
          if (saved) {
            initialMap[pkg.id] = {
              discount_price: saved.discount_price ?? pkg.price,
              original_price: saved.original_price ?? pkg.price,
              discount_badge: saved.discount_badge ?? "🔥 DISCOUNT",
              is_active: saved.is_active ?? false,
            };
          } else {
            initialMap[pkg.id] = {
              discount_price: Math.round(pkg.price * 0.8),
              original_price: pkg.price,
              discount_badge: "🔥 20% OFF",
              is_active: false,
            };
          }
        }
        setDiscountsMap(initialMap);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : "Failed to load settings"))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  // Profile Save
  async function saveProfile(e: React.FormEvent) {
    e.preventDefault();
    if (!token) return;
    setProfileError("");
    setProfileOkMsg("");

    if (profileNewPassword || profileCurrentPassword) {
      if (!profileCurrentPassword) {
        setProfileError("Current password is required to change your password.");
        return;
      }
      if (profileNewPassword.length < 6) {
        setProfileError("New password must be at least 6 characters.");
        return;
      }
      if (profileNewPassword !== profileConfirmPassword) {
        setProfileError("New passwords do not match.");
        return;
      }
    }

    setProfileSaving(true);
    try {
      const payload: { full_name?: string; current_password?: string; new_password?: string } = {};
      if (profileFullName.trim()) payload.full_name = profileFullName.trim();
      if (profileNewPassword) {
        payload.current_password = profileCurrentPassword;
        payload.new_password = profileNewPassword;
      }

      await api.admin.updateMe(token, payload);
      if (adminMe && payload.full_name) {
        setAdminMe({ ...adminMe, fullName: payload.full_name });
      }
      setProfileOkMsg("Profile updated successfully.");
      setProfileCurrentPassword("");
      setProfileNewPassword("");
      setProfileConfirmPassword("");
      setTimeout(() => setProfileOkMsg(""), 4000);
    } catch (err) {
      setProfileError(err instanceof ApiError ? err.message : "Failed to update profile.");
    } finally {
      setProfileSaving(false);
    }
  }

  // Payment Setup Save
  async function savePayment() {
    if (!token) return;
    setSavingPayment(true);
    setError("");
    setOkMsg("");
    try {
      const m = parseFloat(monthlyEtb);
      const s = parseFloat(semesterEtb);
      const mm = parseInt(monthlyMonths, 10);
      const sm = parseInt(semesterMonths, 10);
      if (Number.isNaN(m) || m < 1) throw new Error("Invalid monthly price");
      if (Number.isNaN(s) || s < 1) throw new Error("Invalid semester price");

      const body: Record<string, unknown> = {
        monthlyEtb: m,
        semesterEtb: s,
        monthlyMonths: Number.isNaN(mm) ? 1 : mm,
        semesterMonths: Number.isNaN(sm) ? 4 : sm,
        preferredProvider,
        telebirrEnvironment,
        fastestPaymentMethod,
        expressCheckoutEnabled,
        telebirrManualPhone: telebirrManualPhone.trim(),
        telebirrManualName: telebirrManualName.trim(),
        telebirrManualEnabled,
        cbeAccountNumber: cbeAccountNumber.trim(),
        cbeAccountName: cbeAccountName.trim(),
        cbeReceiveLink: cbeReceiveLink.trim(),
        cbeManualEnabled,
        manualInstructions: manualInstructions.trim(),
        manualPaymentEnabled,
        chapaEnabled,
        telebirrEnabled,
      };

      if (chapaSecretKey.trim()) body.chapaSecretKey = chapaSecretKey.trim();
      if (chapaWebhookSecret.trim()) body.chapaWebhookSecret = chapaWebhookSecret.trim();
      if (telebirrFabricAppId.trim()) body.telebirrFabricAppId = telebirrFabricAppId.trim();
      if (telebirrAppSecret.trim()) body.telebirrAppSecret = telebirrAppSecret.trim();
      if (telebirrMerchantAppId.trim()) body.telebirrMerchantAppId = telebirrMerchantAppId.trim();
      if (telebirrMerchantCode.trim()) body.telebirrMerchantCode = telebirrMerchantCode.trim();
      if (telebirrPrivateKey.trim()) body.telebirrPrivateKey = telebirrPrivateKey.trim();
      if (telebirrPublicKey.trim()) body.telebirrPublicKey = telebirrPublicKey.trim();

      const updated = await api.admin.updatePaymentSettings(token, body);
      setPay(updated as Awaited<ReturnType<typeof api.admin.paymentSettings>>);
      setChapaSecretKey("");
      setChapaWebhookSecret("");
      setTelebirrFabricAppId("");
      setTelebirrAppSecret("");
      setTelebirrMerchantAppId("");
      setTelebirrMerchantCode("");
      setTelebirrPrivateKey("");
      setTelebirrPublicKey("");
      setOkMsg("Payment gateway & checkout settings saved successfully.");
      setTimeout(() => setOkMsg(""), 4000);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not save payment settings");
    } finally {
      setSavingPayment(false);
    }
  }

  // Package Discounts Save
  async function savePackageDiscounts() {
    if (!token) return;
    setSavingDiscounts(true);
    setError("");
    setOkMsg("");
    try {
      const payload: Record<string, unknown> = {
        packageDiscounts: discountsMap,
      };
      const updated = await api.admin.updatePaymentSettings(token, payload);
      setPay(updated as Awaited<ReturnType<typeof api.admin.paymentSettings>>);
      setOkMsg("Package discounts and promotional badges published successfully!");
      setTimeout(() => setOkMsg(""), 4000);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not save package discounts");
    } finally {
      setSavingDiscounts(false);
    }
  }

  // Quick Preset Discounts
  function applyDiscountPreset(percentage: number) {
    const updated = { ...discountsMap };
    for (const pkg of packages) {
      const discountPrice = Math.max(1, Math.round(pkg.price * (1 - percentage / 100)));
      updated[pkg.id] = {
        discount_price: discountPrice,
        original_price: pkg.price,
        discount_badge: `🔥 ${percentage}% OFF`,
        is_active: true,
      };
    }
    setDiscountsMap(updated);
  }

  function clearAllDiscounts() {
    const updated = { ...discountsMap };
    for (const pkg of packages) {
      if (updated[pkg.id]) {
        updated[pkg.id] = {
          ...updated[pkg.id],
          is_active: false,
        };
      }
    }
    setDiscountsMap(updated);
  }

  // Provider Connection Test
  async function testProvider(provider: "chapa" | "telebirr") {
    if (!token) return;
    setTesting(provider);
    setError("");
    setOkMsg("");
    try {
      const res = await api.admin.testPaymentProvider(token, provider);
      if (res.ok) setOkMsg(res.message);
      else setError(res.message);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Connection test failed");
    } finally {
      setTesting(null);
    }
  }

  if (loading && !settings) return <PageLoader />;
  if (!settings || !pay) return <PageError message={error || "No settings found"} />;

  function hint(meta?: { configured: boolean; source: string; hint: string | null }) {
    if (!meta?.configured) return "Not configured";
    return `${meta.hint || "Configured"} (${meta.source})`;
  }

  const SECTIONS = [
    {
      id: "profile" as const,
      label: "Profile & Security",
      icon: Shield,
      desc: "Admin credentials & password",
    },
    {
      id: "payments" as const,
      label: "Payment Gateways & Checkout",
      icon: CreditCard,
      desc: "Fastest checkout & gateways",
    },
    {
      id: "ai" as const,
      label: "AI Providers & Models",
      icon: Sparkles,
      desc: "Interchangeable LLM providers",
    },
    {
      id: "ingestion" as const,
      label: "Content Ingestion",
      icon: KeyRound,
      desc: "Chapter import switch & key",
    },
    {
      id: "discounts" as const,
      label: "Packages & Discounts",
      icon: Tag,
      desc: "Pricing, discounts & badges",
    },
    {
      id: "social" as const,
      label: "Social & Community",
      icon: Share2,
      desc: "Telegram channels & footer",
    },
    {
      id: "system" as const,
      label: "System & Platform",
      icon: SettingsIcon,
      desc: "App environment & catalog",
    },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-4xl mx-auto w-full min-w-0">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-foreground font-display">Admin Settings</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Configure security, express checkout, package discounts, and platform settings.
        </p>
      </div>

      {/* Responsive Section Tab Navigation */}
      <nav
        aria-label="Settings navigation"
        className="flex items-center gap-1.5 p-1.5 bg-secondary/60 rounded-xl border border-border overflow-x-auto no-scrollbar scroll-smooth"
      >
        {SECTIONS.map((sec) => {
          const Icon = sec.icon;
          const isActive = activeSection === sec.id;
          return (
            <button
              key={sec.id}
              type="button"
              onClick={() => {
                setActiveSection(sec.id);
                setError("");
                setOkMsg("");
              }}
              className={`flex items-center gap-2 px-3.5 py-2.5 rounded-lg whitespace-nowrap shrink-0 transition-all text-xs font-semibold ${
                isActive
                  ? "bg-card text-primary border border-border shadow-xs font-bold ring-1 ring-primary/25"
                  : "text-muted-foreground hover:text-foreground hover:bg-card/40"
              }`}
            >
              <Icon size={16} className={isActive ? "text-primary shrink-0" : "text-muted-foreground shrink-0"} />
              <span className="font-display">{sec.label}</span>
            </button>
          );
        })}
      </nav>

      {/* Global Status Feedback */}
      {error && (
        <div className="p-4 text-xs bg-destructive/10 border border-destructive/20 text-destructive rounded-xl flex items-center gap-2.5">
          <AlertCircle size={16} className="shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {okMsg && (
        <div className="p-4 text-xs bg-success/10 border border-success/30 text-success rounded-xl flex items-center gap-2.5">
          <CheckCircle2 size={16} className="shrink-0" />
          <span>{okMsg}</span>
        </div>
      )}

      {/* ─── SECTION 1: PROFILE & SECURITY (Hidden unless active) ────────────── */}
      {activeSection === "profile" && (
        <form onSubmit={saveProfile} className="bg-card rounded-xl border border-border shadow-xs p-6 space-y-6">
          <div className="flex items-center justify-between gap-4 flex-wrap pb-4 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary border border-primary/20 flex items-center justify-center shrink-0">
                <User size={20} />
              </div>
              <div>
                <h2 className="font-semibold text-lg flex items-center gap-2">
                  <span>My Profile & Security</span>
                  {adminMe && (
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                      {adminMe.isSuperAdmin ? "Super Admin" : "Administrator"}
                    </span>
                  )}
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Update your display name and change your administrator login credentials.
                </p>
              </div>
            </div>
            {adminMe?.phone && (
              <div className="text-xs bg-muted/60 px-3 py-1.5 rounded-lg border border-border text-muted-foreground">
                Phone: <span className="font-medium text-foreground">{adminMe.phone}</span>
              </div>
            )}
          </div>

          {profileError && (
            <div className="p-3 text-xs bg-destructive/10 border border-destructive/20 text-destructive rounded-lg flex items-center gap-2">
              <AlertCircle size={15} />
              <span>{profileError}</span>
            </div>
          )}
          {profileOkMsg && (
            <div className="p-3 text-xs bg-success/10 border border-success/30 text-success rounded-lg flex items-center gap-2">
              <CheckCircle2 size={15} />
              <span>{profileOkMsg}</span>
            </div>
          )}

          <div className="space-y-4 max-w-lg">
            <label className="text-xs font-semibold text-foreground space-y-1.5 block">
              <span>Full Name</span>
              <input
                type="text"
                required
                value={profileFullName}
                onChange={(e) => setProfileFullName(e.target.value)}
                placeholder="Your full name"
                className="w-full rounded-lg border border-border px-3 py-2 bg-background text-sm text-foreground"
              />
            </label>

            <div className="pt-3 border-t border-border/70 space-y-3">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                <KeyRound size={14} className="text-primary" />
                <span>Change Password</span>
              </div>

              <label className="text-xs font-medium text-muted-foreground space-y-1 block">
                <span>Current Password</span>
                <div className="relative">
                  <input
                    type={showCurrentPass ? "text" : "password"}
                    value={profileCurrentPassword}
                    onChange={(e) => setProfileCurrentPassword(e.target.value)}
                    placeholder="Enter current password to authorize changes"
                    className="w-full rounded-lg border border-border px-3 py-2 bg-background text-sm text-foreground pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPass(!showCurrentPass)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showCurrentPass ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </label>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="text-xs font-medium text-muted-foreground space-y-1 block">
                  <span>New Password</span>
                  <div className="relative">
                    <input
                      type={showNewPass ? "text" : "password"}
                      value={profileNewPassword}
                      onChange={(e) => setProfileNewPassword(e.target.value)}
                      placeholder="Min. 6 characters"
                      className="w-full rounded-lg border border-border px-3 py-2 bg-background text-sm text-foreground pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPass(!showNewPass)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showNewPass ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </label>
                <label className="text-xs font-medium text-muted-foreground space-y-1 block">
                  <span>Confirm New Password</span>
                  <input
                    type="password"
                    value={profileConfirmPassword}
                    onChange={(e) => setProfileConfirmPassword(e.target.value)}
                    placeholder="Repeat new password"
                    className="w-full rounded-lg border border-border px-3 py-2 bg-background text-sm text-foreground"
                  />
                </label>
              </div>
            </div>
          </div>

          <div className="pt-2 border-t border-border">
            <button
              type="submit"
              disabled={profileSaving}
              className="btn btn-primary text-xs px-5 py-2.5 disabled:opacity-50"
            >
              {profileSaving ? "Saving…" : "Save Profile & Password"}
            </button>
          </div>
        </form>
      )}

      {/* ─── SECTION 2: PAYMENT GATEWAYS & FAST CHECKOUT (Hidden unless active) ── */}
      {activeSection === "payments" && (
        <div className="space-y-6">
          {/* Fastest Payment Method Priority Card */}
          <div className="bg-gradient-to-br from-card to-secondary/30 rounded-xl border border-primary/30 shadow-xs p-6 space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary text-on-accent flex items-center justify-center shrink-0 shadow-xs">
                <Zap size={20} />
              </div>
              <div>
                <h2 className="font-semibold text-lg flex items-center gap-2">
                  <span>Fastest Payment Method & Express Priority</span>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                    Recommended
                  </span>
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Configure which checkout option is highlighted as the fastest way to pay so students activate instantly.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 min-w-0">
              <div className="min-w-0">
                <label className="block text-xs font-bold text-foreground mb-1.5">
                  Designated Fastest Method
                </label>
                <select
                  value={fastestPaymentMethod}
                  onChange={(e) => setFastestPaymentMethod(e.target.value)}
                  className="w-full text-xs rounded-lg border border-border bg-background px-3 py-2.5 text-foreground font-medium max-w-full truncate"
                >
                  <option value="telebirr_direct">
                    ⚡ Telebirr Direct (Instant Phone Transfer & Auto Verification) [Fastest]
                  </option>
                  <option value="cbe_birr">
                    ⚡ CBE Birr / Bank Transfer (Real-Time MB-Receipt Verification)
                  </option>
                  <option value="chapa">💳 Chapa Online (Cards, Bank & Telebirr Web Gateway)</option>
                  <option value="telebirr_h5">📱 Telebirr H5 Gateway (Official Fabric Web API)</option>
                  <option value="auto">Smart Automatic Prioritization</option>
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  Telebirr Direct is zero-fee and activates immediately via the Ethio Telecom receipt portal.
                </p>
              </div>

              <div className="bg-background/80 border border-border rounded-xl p-4 flex items-center justify-between gap-3 min-w-0">
                <div className="min-w-0">
                  <p className="text-xs font-bold text-foreground">Express Checkout Mode</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Auto-selects and badges the fastest payment option in the student checkout view.
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer shrink-0">
                  <input
                    type="checkbox"
                    checked={expressCheckoutEnabled}
                    onChange={(e) => setExpressCheckoutEnabled(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
            </div>
          </div>

          {/* Manual Zero-Fee Transfer Numbers */}
          <div className="bg-card rounded-xl border border-border shadow-xs p-6 space-y-5">
            <div>
              <h2 className="font-semibold text-lg">Direct Manual Transfers (Zero Transaction Fees)</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Students transfer directly to your verified merchant phone/account. System verifies transactions in real time.
              </p>
            </div>

            {/* Telebirr Direct */}
            <div className="border border-border rounded-xl p-4.5 space-y-4 bg-secondary/20">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Smartphone size={18} className="text-primary" />
                  <div>
                    <p className="font-semibold text-sm">Telebirr Direct Transfer</p>
                    <p className="text-xs text-muted-foreground">
                      Receives transfer from student&apos;s Telebirr app. Auto-verified via Ethio Telecom receipt check.
                    </p>
                  </div>
                </div>
                <label className="flex items-center gap-2 text-xs font-semibold cursor-pointer select-none bg-background px-3 py-1.5 rounded-lg border border-border shadow-xs">
                  <input
                    type="checkbox"
                    checked={telebirrManualEnabled}
                    onChange={(e) => setTelebirrManualEnabled(e.target.checked)}
                    className="rounded border-border text-primary focus:ring-primary h-4 w-4"
                  />
                  <span>{telebirrManualEnabled ? "Telebirr: ON" : "Telebirr: OFF"}</span>
                </label>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <label className="text-xs font-medium text-muted-foreground space-y-1 block">
                  <span>Recipient Phone Number</span>
                  <input
                    type="text"
                    value={telebirrManualPhone}
                    onChange={(e) => setTelebirrManualPhone(e.target.value)}
                    placeholder="e.g. 0967344843"
                    className="w-full rounded-lg border border-border px-3 py-2 bg-background font-mono text-xs text-foreground"
                  />
                </label>
                <label className="text-xs font-medium text-muted-foreground space-y-1 block">
                  <span>Recipient Account Name</span>
                  <input
                    type="text"
                    value={telebirrManualName}
                    onChange={(e) => setTelebirrManualName(e.target.value)}
                    placeholder="e.g. Atinaf"
                    className="w-full rounded-lg border border-border px-3 py-2 bg-background text-xs text-foreground"
                  />
                </label>
              </div>
            </div>

            {/* CBE Bank Transfer */}
            <div className="border border-border rounded-xl p-4.5 space-y-4 bg-secondary/20">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Building2 size={18} className="text-primary" />
                  <div>
                    <p className="font-semibold text-sm">Commercial Bank of Ethiopia (CBE)</p>
                    <p className="text-xs text-muted-foreground">
                      Supports CBE Birr and CBE Mobile Banking. Real-time verification via official MB-receipt link.
                    </p>
                  </div>
                </div>
                <label className="flex items-center gap-2 text-xs font-semibold cursor-pointer select-none bg-background px-3 py-1.5 rounded-lg border border-border shadow-xs">
                  <input
                    type="checkbox"
                    checked={cbeManualEnabled}
                    onChange={(e) => setCbeManualEnabled(e.target.checked)}
                    className="rounded border-border text-primary focus:ring-primary h-4 w-4"
                  />
                  <span>{cbeManualEnabled ? "CBE: ON" : "CBE: OFF"}</span>
                </label>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <label className="text-xs font-medium text-muted-foreground space-y-1 block">
                  <span>CBE Account Number</span>
                  <input
                    type="text"
                    value={cbeAccountNumber}
                    onChange={(e) => setCbeAccountNumber(e.target.value)}
                    placeholder="e.g. 1000123456789"
                    className="w-full rounded-lg border border-border px-3 py-2 bg-background font-mono text-xs text-foreground"
                  />
                </label>
                <label className="text-xs font-medium text-muted-foreground space-y-1 block">
                  <span>Account Holder Name</span>
                  <input
                    type="text"
                    value={cbeAccountName}
                    onChange={(e) => setCbeAccountName(e.target.value)}
                    placeholder="Account holder name"
                    className="w-full rounded-lg border border-border px-3 py-2 bg-background text-xs text-foreground"
                  />
                </label>
              </div>
            </div>

            {/* Instructions */}
            <label className="text-xs font-medium text-muted-foreground space-y-1 block">
              <span>Custom Student Payment Instructions</span>
              <textarea
                rows={2}
                value={manualInstructions}
                onChange={(e) => setManualInstructions(e.target.value)}
                placeholder="Optional instructions shown to students on the transfer screen"
                className="w-full rounded-lg border border-border px-3 py-2 bg-background text-xs text-foreground"
              />
            </label>
          </div>

          {/* Automated Gateways (Chapa & Telebirr H5) */}
          <div className="bg-card rounded-xl border border-border shadow-xs p-6 space-y-5">
            <div>
              <h2 className="font-semibold text-lg">Automated Online Payment Gateways</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Manage API credentials for card processing, digital web wallets, and automated callbacks.
              </p>
            </div>

            {/* Chapa Gateway */}
            <div className="border border-border rounded-xl p-4.5 space-y-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-semibold text-sm flex items-center gap-2">
                    <span>Chapa Payment Gateway</span>
                    <span
                      className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        !chapaEnabled
                          ? "bg-muted text-muted-foreground border border-border"
                          : pay.chapa.configured
                          ? "bg-success/10 text-success border border-success/30"
                          : "bg-warning/10 text-warning border border-warning/30"
                      }`}
                    >
                      {!chapaEnabled
                        ? "Disabled"
                        : pay.chapa.configured
                        ? `Active · ${hint(pay.chapa.secretKey)}`
                        : "Keys Required"}
                    </span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Online cards, CBE Birr, and digital checkout via Chapa.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-2 text-xs font-semibold cursor-pointer select-none bg-background px-3 py-1.5 rounded-lg border border-border shadow-xs">
                    <input
                      type="checkbox"
                      checked={chapaEnabled}
                      onChange={(e) => setChapaEnabled(e.target.checked)}
                      className="rounded border-border text-primary focus:ring-primary h-4 w-4"
                    />
                    <span>{chapaEnabled ? "Chapa: ON" : "Chapa: OFF"}</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => testProvider("chapa")}
                    disabled={Boolean(testing)}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-border bg-background hover:bg-muted disabled:opacity-50"
                  >
                    {testing === "chapa" ? "Testing…" : "Test Connection"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <label className="text-xs font-medium text-muted-foreground space-y-1 block">
                  <span>Secret Key {pay.chapa.secretKey.hint ? `(${pay.chapa.secretKey.hint})` : ""}</span>
                  <input
                    type="password"
                    autoComplete="off"
                    placeholder="Leave blank to keep current"
                    value={chapaSecretKey}
                    onChange={(e) => setChapaSecretKey(e.target.value)}
                    className="w-full rounded-lg border border-border px-3 py-2 bg-background font-mono text-xs text-foreground"
                  />
                </label>
                <label className="text-xs font-medium text-muted-foreground space-y-1 block">
                  <span>Webhook Secret {pay.chapa.webhookSecret.hint ? `(${pay.chapa.webhookSecret.hint})` : ""}</span>
                  <input
                    type="password"
                    autoComplete="off"
                    placeholder="Leave blank to keep current"
                    value={chapaWebhookSecret}
                    onChange={(e) => setChapaWebhookSecret(e.target.value)}
                    className="w-full rounded-lg border border-border px-3 py-2 bg-background font-mono text-xs text-foreground"
                  />
                </label>
              </div>
            </div>

            {/* Telebirr H5 Gateway */}
            <div className="border border-border rounded-xl p-4.5 space-y-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-semibold text-sm flex items-center gap-2">
                    <span>Telebirr H5 Automated Gateway</span>
                    <span
                      className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        !telebirrEnabled
                          ? "bg-muted text-muted-foreground border border-border"
                          : pay.telebirr.configured
                          ? "bg-success/10 text-success border border-success/30"
                          : "bg-warning/10 text-warning border border-warning/30"
                      }`}
                    >
                      {!telebirrEnabled
                        ? "Disabled"
                        : pay.telebirr.configured
                        ? "Active · Gateway Ready"
                        : "Optional Gateway"}
                    </span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Official Ethio Telecom Fabric API automated H5 integration.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-2 text-xs font-semibold cursor-pointer select-none bg-background px-3 py-1.5 rounded-lg border border-border shadow-xs">
                    <input
                      type="checkbox"
                      checked={telebirrEnabled}
                      onChange={(e) => setTelebirrEnabled(e.target.checked)}
                      className="rounded border-border text-primary focus:ring-primary h-4 w-4"
                    />
                    <span>{telebirrEnabled ? "H5: ON" : "H5: OFF"}</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => testProvider("telebirr")}
                    disabled={Boolean(testing)}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-border bg-background hover:bg-muted disabled:opacity-50"
                  >
                    {testing === "telebirr" ? "Testing…" : "Test Connection"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                {[
                  ["Fabric App ID", telebirrFabricAppId, setTelebirrFabricAppId, pay.telebirr.fabricAppId.hint],
                  ["App Secret", telebirrAppSecret, setTelebirrAppSecret, pay.telebirr.appSecret.hint],
                  ["Merchant App ID", telebirrMerchantAppId, setTelebirrMerchantAppId, pay.telebirr.merchantAppId.hint],
                  ["Merchant Code", telebirrMerchantCode, setTelebirrMerchantCode, pay.telebirr.merchantCode.hint],
                ].map(([label, val, setter, h]) => (
                  <label key={label as string} className="text-xs font-medium text-muted-foreground space-y-1 block">
                    <span>
                      {label as string} {h ? `(${h})` : ""}
                    </span>
                    <input
                      type="password"
                      autoComplete="off"
                      placeholder="Leave blank to keep current"
                      value={val as string}
                      onChange={(e) => (setter as (s: string) => void)(e.target.value)}
                      className="w-full rounded-lg border border-border px-3 py-2 bg-background font-mono text-xs text-foreground"
                    />
                  </label>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <label className="text-xs font-medium text-muted-foreground space-y-1 block">
                  <span>Private Key {pay.telebirr.privateKey.hint ? `(${pay.telebirr.privateKey.hint})` : ""}</span>
                  <textarea
                    rows={2}
                    placeholder="Paste PEM or base64 key — leave blank to keep current"
                    value={telebirrPrivateKey}
                    onChange={(e) => setTelebirrPrivateKey(e.target.value)}
                    className="w-full rounded-lg border border-border px-3 py-2 bg-background font-mono text-xs text-foreground"
                  />
                </label>
                <label className="text-xs font-medium text-muted-foreground space-y-1 block">
                  <span>Environment</span>
                  <select
                    value={telebirrEnvironment}
                    onChange={(e) => setTelebirrEnvironment(e.target.value)}
                    className="w-full text-xs rounded-lg border border-border bg-background px-3 py-2 text-foreground"
                  >
                    <option value="test">Test / Sandbox Environment</option>
                    <option value="production">Production / Live</option>
                  </select>
                </label>
              </div>
            </div>

            <div className="pt-2 border-t border-border flex justify-end">
              <button
                type="button"
                onClick={savePayment}
                disabled={savingPayment}
                className="btn btn-primary text-xs px-6 py-2.5 shadow-xs disabled:opacity-50"
              >
                {savingPayment ? "Saving Gateways…" : "Save Gateway & Checkout Settings"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── SECTION 3: PACKAGES & DISCOUNTS (Hidden unless active) ─────────── */}
      {activeSection === "discounts" && (
        <div className="space-y-6">
          {/* Base Rates Overview */}
          <div className="bg-card rounded-xl border border-border shadow-xs p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary border border-primary/20 flex items-center justify-center shrink-0">
                <Percent size={20} />
              </div>
              <div>
                <h2 className="font-semibold text-lg">Base Subscription Rates</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Fallback global prices used when students select standard monthly or semester billing.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              <div className="bg-secondary/20 p-4 rounded-xl border border-border space-y-2">
                <p className="text-xs font-bold text-foreground">Monthly Plan Base Rate</p>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={monthlyEtb}
                    onChange={(e) => setMonthlyEtb(e.target.value)}
                    className="w-32 rounded-lg border border-border px-3 py-1.5 bg-background text-sm font-bold text-foreground"
                  />
                  <span className="text-xs text-muted-foreground font-semibold">ETB</span>
                </div>
                <p className="text-xs text-muted-foreground">Standard monthly recurring rate.</p>
              </div>

              <div className="bg-secondary/20 p-4 rounded-xl border border-border space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold text-foreground">Semester Plan Base Rate</p>
                  {pay.semesterSavings && (
                    <span className="text-xs font-bold text-success bg-success/10 px-2 py-0.5 rounded-full border border-success/30">
                      {pay.semesterSavings}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={semesterEtb}
                    onChange={(e) => setSemesterEtb(e.target.value)}
                    className="w-32 rounded-lg border border-border px-3 py-1.5 bg-background text-sm font-bold text-foreground"
                  />
                  <span className="text-xs text-muted-foreground font-semibold">ETB</span>
                </div>
                <p className="text-xs text-muted-foreground">Standard 4-month semester rate.</p>
              </div>
            </div>
          </div>

          {/* Package Discount Manager */}
          <div className="bg-card rounded-xl border border-border shadow-xs p-6 space-y-5">
            <div className="flex items-center justify-between gap-4 flex-wrap pb-4 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary text-on-accent flex items-center justify-center shrink-0 shadow-xs">
                  <Tag size={20} />
                </div>
                <div>
                  <h2 className="font-semibold text-lg flex items-center gap-2">
                    <span>Package Discounts & Promotions</span>
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                      Live Promo Controls
                    </span>
                  </h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Apply discounts to specific packages with strikethrough original prices and custom promo badges.
                  </p>
                </div>
              </div>

              {/* Quick Presets */}
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-xs text-muted-foreground font-medium mr-1">Quick Presets:</span>
                <button
                  type="button"
                  onClick={() => applyDiscountPreset(15)}
                  className="text-xs font-semibold px-2.5 py-1 rounded-md bg-secondary hover:bg-secondary/80 border border-border"
                >
                  -15%
                </button>
                <button
                  type="button"
                  onClick={() => applyDiscountPreset(25)}
                  className="text-xs font-semibold px-2.5 py-1 rounded-md bg-secondary hover:bg-secondary/80 border border-border"
                >
                  -25%
                </button>
                <button
                  type="button"
                  onClick={() => applyDiscountPreset(30)}
                  className="text-xs font-semibold px-2.5 py-1 rounded-md bg-secondary hover:bg-secondary/80 border border-border"
                >
                  -30%
                </button>
                <button
                  type="button"
                  onClick={clearAllDiscounts}
                  className="text-xs font-semibold px-2.5 py-1 rounded-md text-destructive bg-destructive/10 hover:bg-destructive/20 border border-destructive/20"
                >
                  Reset
                </button>
              </div>
            </div>

            {packages.length === 0 ? (
              <div className="p-8 text-center text-xs text-muted-foreground border border-dashed border-border rounded-xl">
                No premium packages exist yet. Create packages in the{" "}
                <Link to="/admin/packages" className="text-primary underline font-semibold">
                  Packages Manager
                </Link>{" "}
                first.
              </div>
            ) : (
              <div className="space-y-4">
                {packages.map((pkg) => {
                  const disc = discountsMap[pkg.id] || {
                    discount_price: pkg.price,
                    original_price: pkg.price,
                    discount_badge: "🔥 DISCOUNT",
                    is_active: false,
                  };

                  const calculatedPercent =
                    disc.original_price > 0 && disc.discount_price < disc.original_price
                      ? Math.round((1 - disc.discount_price / disc.original_price) * 100)
                      : 0;

                  return (
                    <div
                      key={pkg.id}
                      className={`border rounded-xl p-4.5 transition ${
                        disc.is_active
                          ? "bg-primary/5 border-primary/40 shadow-xs"
                          : "bg-card border-border hover:border-border/80"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3 flex-wrap mb-3.5 min-w-0">
                        <div className="flex items-center gap-3 min-w-0">
                          <label className="relative inline-flex items-center cursor-pointer shrink-0">
                            <input
                              type="checkbox"
                              checked={disc.is_active}
                              onChange={(e) =>
                                setDiscountsMap((prev) => ({
                                  ...prev,
                                  [pkg.id]: {
                                    ...disc,
                                    is_active: e.target.checked,
                                  },
                                }))
                              }
                              className="sr-only peer"
                            />
                            <div className="w-9 h-5 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
                          </label>
                          <div className="min-w-0">
                            <p className="font-bold text-sm text-foreground flex items-center gap-2 flex-wrap">
                              <span className="truncate">{pkg.name}</span>
                              <span className="text-xs font-normal text-muted-foreground shrink-0">
                                ({pkg.plan_type} &middot; Semesters: {pkg.semesters_covered?.join(", ")})
                              </span>
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Base Price: <span className="font-semibold text-foreground">{pkg.price} ETB</span>
                              {pkg.stream ? ` · Stream: ${pkg.stream}` : " · All Streams"}
                            </p>
                          </div>
                        </div>

                        {/* Live Student Preview Badge */}
                        <div className="flex items-center gap-2 bg-background px-3 py-1.5 rounded-lg border border-border shadow-xs shrink-0 max-w-full">
                          <span className="text-xs text-muted-foreground font-medium shrink-0">Student View:</span>
                          {disc.is_active ? (
                            <div className="flex items-center gap-1.5 text-xs flex-wrap">
                              <span className="line-through text-muted-foreground font-medium shrink-0">
                                {disc.original_price} ETB
                              </span>
                              <span className="font-bold text-primary font-display shrink-0">
                                {disc.discount_price} ETB
                              </span>
                              <span className="text-xs font-bold px-1.5 py-0.5 rounded-full bg-primary text-on-accent shrink-0">
                                {disc.discount_badge || `${calculatedPercent}% OFF`}
                              </span>
                            </div>
                          ) : (
                            <span className="text-xs font-semibold text-foreground shrink-0">{pkg.price} ETB</span>
                          )}
                        </div>
                      </div>

                      {disc.is_active && (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-3 border-t border-border/60 min-w-0">
                          <div className="min-w-0">
                            <label className="block text-xs font-bold text-foreground mb-1">
                              Discounted Price (ETB)
                            </label>
                            <input
                              type="number"
                              min={1}
                              value={disc.discount_price}
                              onChange={(e) =>
                                setDiscountsMap((prev) => ({
                                  ...prev,
                                  [pkg.id]: {
                                    ...disc,
                                    discount_price: parseFloat(e.target.value) || 0,
                                  },
                                }))
                              }
                              className="w-full text-xs rounded-lg border border-border bg-background px-3 py-2 text-foreground font-bold"
                            />
                            <p className="text-xs text-muted-foreground mt-0.5">
                              Student will be charged this exact amount.
                            </p>
                          </div>

                          <div className="min-w-0">
                            <label className="block text-xs font-bold text-foreground mb-1">
                              Strikethrough Original (ETB)
                            </label>
                            <input
                              type="number"
                              min={1}
                              value={disc.original_price}
                              onChange={(e) =>
                                setDiscountsMap((prev) => ({
                                  ...prev,
                                  [pkg.id]: {
                                    ...disc,
                                    original_price: parseFloat(e.target.value) || 0,
                                  },
                                }))
                              }
                              className="w-full text-xs rounded-lg border border-border bg-background px-3 py-2 text-foreground"
                            />
                            <p className="text-xs text-muted-foreground mt-0.5">
                              Displayed with strikethrough next to price.
                            </p>
                          </div>

                          <div className="min-w-0">
                            <label className="block text-xs font-bold text-foreground mb-1">
                              Promotional Badge Text
                            </label>
                            <input
                              type="text"
                              value={disc.discount_badge}
                              onChange={(e) =>
                                setDiscountsMap((prev) => ({
                                  ...prev,
                                  [pkg.id]: {
                                    ...disc,
                                    discount_badge: e.target.value,
                                  },
                                }))
                              }
                              placeholder="e.g. 🔥 25% OFF"
                              className="w-full text-xs rounded-lg border border-border bg-background px-3 py-2 text-foreground"
                            />
                            <p className="text-xs text-muted-foreground mt-0.5">
                              Rendered as a top badge on package card.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="pt-3 border-t border-border flex justify-end">
              <button
                type="button"
                onClick={savePackageDiscounts}
                disabled={savingDiscounts}
                className="btn btn-primary text-xs px-6 py-2.5 shadow-xs disabled:opacity-50"
              >
                {savingDiscounts ? "Publishing Discounts…" : "Save & Publish Package Discounts"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── SECTION 3.5: AI PROVIDERS & MODELS (Hidden unless active) ────────── */}
      {activeSection === "ai" && token && <AiProvidersSettingsSection token={token} />}

      {activeSection === "ingestion" && token && <IngestionSettingsSection token={token} />}

      {/* ─── SECTION 4: SOCIAL & COMMUNITY LINKS (Hidden unless active) ──────── */}
      {activeSection === "social" && token && <SocialLinksSettingsSection token={token} />}

      {/* ─── SECTION 5: SYSTEM & PLATFORM OVERVIEW (Hidden unless active) ────── */}
      {activeSection === "system" && (
        <div className="bg-card rounded-xl border border-border shadow-xs divide-y divide-border">
          <div className="px-6 py-5">
            <h2 className="font-semibold text-base mb-1">Platform Environment</h2>
            <p className="text-xs text-muted-foreground mb-3">Core system deployment and timing configurations.</p>
            <dl className="text-xs space-y-2 text-muted-foreground">
              <div className="flex justify-between py-1 border-b border-border/40">
                <dt className="font-medium text-foreground">Application Name</dt>
                <dd className="font-mono">{settings.appName}</dd>
              </div>
              <div className="flex justify-between py-1 border-b border-border/40">
                <dt className="font-medium text-foreground">Environment</dt>
                <dd className="font-semibold capitalize text-primary">{settings.appEnv}</dd>
              </div>
              <div className="flex justify-between py-1">
                <dt className="font-medium text-foreground">OTP Login Expiry</dt>
                <dd>{settings.telegramOtpMinutes} minutes</dd>
              </div>
            </dl>
          </div>

          <div className="px-6 py-5 space-y-2">
            <h2 className="font-semibold text-base">Telegram Bot Uploads</h2>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Curated exam and resource submissions operate through the official bot{" "}
              <span className="font-mono font-semibold text-foreground">
                {settings.botUsername ? `@${settings.botUsername.replace("@", "")}` : "bot (not set)"}
              </span>
              . Bot commands include deep-links to university catalogs.
            </p>
          </div>

          <div className="px-6 py-5 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h2 className="font-semibold text-base">Universities & Academic Catalog</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {(settings.universities || []).length} universities &middot; {(settings.courses || []).length} courses actively published.
              </p>
            </div>
            <Link
              to="/admin/catalog"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-lg bg-secondary text-foreground hover:bg-secondary/80 border border-border transition shadow-xs"
            >
              <span>Open Catalog Manager</span>
              <ExternalLink size={13} />
            </Link>
          </div>

          {settings.isSuperAdmin && (
            <div className="px-6 py-5 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h2 className="font-semibold text-base">Administrator Accounts & Access</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Manage privileged administrator logins, permissions, and audit trails.
                </p>
              </div>
              <Link
                to="/admin/admins"
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-lg bg-primary text-on-accent hover:opacity-90 transition shadow-xs"
              >
                <span>Manage Administrators</span>
                <ExternalLink size={13} />
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AdminSettings;
